import { secureStorage } from '@/core/storage';
import { getCurrentLanguage } from '@/core/utils/i18n';
import {
  type AdhaUaePassRawCallback,
  ADHA_UAE_PASS_FAILURE_HOST,
  ADHA_UAE_PASS_SUCCESS_HOST,
  getAdhaAcrValuesForNative,
  getAdhaUaePassProviderHost,
  getNativeAdhaClientId,
  getNativeAdhaRedirectUriFromEnv,
  getUaePassAndroidPackageIds,
  getUaePassAppSchemeFallbackName,
  getUaePassAppSchemeName,
  getUaePassAppSchemeNames,
} from '@/modules/auth/constants/adhaUaePass';
import { AdhaUaePassErrorCode, AdhaUaePassErrorDescription } from '@/modules/auth/constants/adhaUaePassError';
import { adhaUaePassService } from '@/modules/auth/services/adhaUaePassService';
import { AppState, Linking, Platform } from 'react-native';

let debugLinkingInstalled = false;

/**
 * Catch every OS deep link / app-foreground event, even if the WebView unmounted.
 * Call once when the native UAE Pass flow starts.
 */
export function installAdhaUaePassDebugListeners(): void {
  if (Platform.OS === 'web') {
    return;
  }

  Linking.getInitialURL()
    .then((url) => {
      if (url) {
        rememberRuntimeLink(url);
      }
    })
    .catch(() => undefined);

  const linkingUri = getConstantsLinkingUri();
  if (linkingUri) {
    rememberRuntimeLink(linkingUri);
  }

  if (debugLinkingInstalled) {
    return;
  }
  debugLinkingInstalled = true;

  Linking.addEventListener('url', ({ url }) => {
    rememberRuntimeLink(url);
  });

  AppState.addEventListener('change', (nextState) => {
    if (nextState === 'active') {
      Linking.getInitialURL()
        .then((url) => {
          if (url) {
            rememberRuntimeLink(url);
          }
        })
        .catch(() => undefined);
      notifyForegroundResume();
    }
  });
}

export function getNativeAdhaRedirectUri(): string {
  return getNativeAdhaRedirectUriFromEnv();
}

let uaePassAppInstalled = false;

export function isUaePassAppAvailable(): boolean {
  return uaePassAppInstalled;
}

function anyInstalled(result: Record<string, boolean> | null | undefined): boolean {
  if (!result) {
    return false;
  }
  return Object.values(result).some(Boolean);
}

/** True when the UAE Pass app from env is installed (iOS scheme / Android package). */
export async function detectUaePassAppInstalled(): Promise<boolean> {
  if (Platform.OS === 'web') {
    uaePassAppInstalled = false;
    return false;
  }

  try {
    const { checkInstalledApps } = require('expo-check-installed-apps') as {
      checkInstalledApps: (targets: string[]) => Promise<Record<string, boolean>>;
    };
    const targets =
      Platform.OS === 'android'
        ? getUaePassAndroidPackageIds()
        : getUaePassAppSchemeNames();
    if (targets.length > 0) {
      const result = await checkInstalledApps(targets);
      if (anyInstalled(result)) {
        uaePassAppInstalled = true;
        return true;
      }
    }
  } catch {
    // Module missing on some builds — fall through to scheme check.
  }

  if (Platform.OS === 'ios') {
    for (const name of getUaePassAppSchemeNames()) {
      try {
        if (await Linking.canOpenURL(`${name}://`)) {
          uaePassAppInstalled = true;
          return true;
        }
      } catch {
        // Keep checking remaining schemes.
      }
    }
  }

  uaePassAppInstalled = false;
  return false;
}

export async function startNativeAdhaUaePassAuthorization(): Promise<{
  authUrl: string;
  redirectUri: string;
}> {
  const redirectUri = getNativeAdhaRedirectUri();
  const appInstalled = await detectUaePassAppInstalled();
  const storedLanguage = await getCurrentLanguage();
  const started = adhaUaePassService.startNativeAuthorization({
    acrValues: getAdhaAcrValuesForNative(appInstalled),
    clientId: getNativeAdhaClientId(),
    redirectUri,
    uiLocale: storedLanguage === 'ar' ? 'ar' : 'en',
  });

  installAdhaUaePassDebugListeners();


  return started;
}

function firstQueryValue(url: string, key: string): string {
  try {
    return new URL(url).searchParams.get(key) ?? '';
  } catch {
    const escaped = key.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
    const match = new RegExp(`[?&]${escaped}=([^&]*)`).exec(url);
    if (!match?.[1]) {
      return '';
    }
    return decodeURIComponent(match[1].replaceAll('+', ' '));
  }
}

export function parseNativeCallbackFromUrl(url: string): AdhaUaePassRawCallback {
  const nestedResume = firstQueryValue(url, 'url');
  const providerHost = getAdhaUaePassProviderHost();
  const shouldParseNested =
    Boolean(nestedResume) &&
    nestedResume !== url &&
    ((providerHost && nestedResume.includes(providerHost)) ||
      isUaePassAppScheme(nestedResume) ||
      /[?&](code|accessCode|access_code|error)=/.test(nestedResume));
  const fromNested = shouldParseNested ? parseNativeCallbackFromUrl(nestedResume) : {};

  const code =
    firstQueryValue(url, 'code') ||
    firstQueryValue(url, 'accessCode') ||
    firstQueryValue(url, 'access_code') ||
    fromNested.code;
  const state = firstQueryValue(url, 'state') || fromNested.state;
  const error = firstQueryValue(url, 'error') || fromNested.error;
  const errorDescription =
    firstQueryValue(url, 'error_description') || fromNested.error_description;

  const parsed: AdhaUaePassRawCallback = {
    ...(code ? { code } : {}),
    ...(state ? { state } : {}),
    ...(error ? { error } : {}),
    ...(errorDescription ? { error_description: errorDescription } : {}),
  };

  if (isAdhaFailureReturnUrl(url) && !parsed.error) {
    parsed.error = AdhaUaePassErrorCode.CANCELLED_ON_APP;
    parsed.error_description = AdhaUaePassErrorDescription.APP;
  }


  return parsed;
}

export function isUaePassAppScheme(url: string): boolean {
  const lower = url.toLowerCase();
  if (getUaePassAppSchemeNames().some((name) => lower.startsWith(`${name}://`))) {
    return true;
  }
  if (!url.startsWith('intent://')) {
    return false;
  }
  return getUaePassAppSchemeNames().some((name) => lower.includes(name));
}

function getBloomAppScheme(): string {
  return runtimeAppScheme || process.env.EXPO_PUBLIC_APP_SCHEME || '';
}

function getIgnoredRuntimeSchemes(): Set<string> {
  return new Set(['http', 'https', 'about', 'file', 'intent', ...getUaePassAppSchemeNames()]);
}

/** Scheme / launch URL that actually opened this process (Expo Dev Client ≠ app.config scheme). */
let runtimeAppScheme: string | null = null;
let runtimeLaunchUrl: string | null = null;
let authenticatorOpened = false;
/** fresh-start: user tapped login. awaiting-return: authenticator is open. logging-in: Android code POST in flight. */
let nativeFlowPhase: 'idle' | 'fresh-start' | 'awaiting-return' | 'logging-in' = 'idle';
const foregroundResumeListeners = new Set<() => void>();

function getConstantsLinkingUri(): string {
  try {
    const Constants = require('expo-constants').default as {
      linkingUri?: string;
      linkingUrl?: string;
    };
    return Constants.linkingUri || Constants.linkingUrl || '';
  } catch {
    return '';
  }
}

export function rememberRuntimeLink(url: string): void {
  if (!url) {
    return;
  }
  const scheme = url.split(':')[0]?.toLowerCase();
  if (!scheme || getIgnoredRuntimeSchemes().has(scheme)) {
    return;
  }
  if (runtimeAppScheme !== scheme) {
    runtimeAppScheme = scheme;
  }
  if (url.includes('expo-development-client')) {
    const metro = firstQueryValue(url, 'url');
    const metroBase = (metro || '').split('/--/')[0];
    const base = url.split('?')[0];
    runtimeLaunchUrl = metroBase
      ? `${base}?url=${encodeURIComponent(metroBase)}`
      : `${base}`;
  }
}

let nativeWebViewSessionId = '';
let adhaCodeLoginInFlight: { code: string; promise: Promise<void> } | null = null;

export function getAdhaCodeLoginInFlight(code: string): Promise<void> | null {
  const trimmed = code.trim();
  if (!trimmed || adhaCodeLoginInFlight?.code !== trimmed) {
    return null;
  }
  return adhaCodeLoginInFlight.promise;
}

export function getAnyAdhaCodeLoginInFlight(): Promise<void> | null {
  return adhaCodeLoginInFlight?.promise ?? null;
}

export function isAdhaCodeLoginInProgress(): boolean {
  return nativeFlowPhase === 'logging-in' || Boolean(adhaCodeLoginInFlight);
}

export function markAdhaCodeLoginInProgress(): void {
  nativeFlowPhase = 'logging-in';
  authenticatorOpened = false;
}

export function setAdhaCodeLoginInFlight(code: string, promise: Promise<void>): void {
  const trimmed = code.trim();
  if (Platform.OS === 'android') {
    nativeFlowPhase = 'logging-in';
    authenticatorOpened = false;
  }
  const tracked = promise.finally(() => {
    if (adhaCodeLoginInFlight?.code === trimmed) {
      adhaCodeLoginInFlight = null;
    }
  });
  adhaCodeLoginInFlight = { code: trimmed, promise: tracked };
}

export function beginFreshNativeAdhaLogin(): void {
  nativeFlowPhase = 'fresh-start';
  authenticatorOpened = false;
  nativeWebViewSessionId = String(Date.now());
  storedAuthenticatorUrls = null;
  persistResumeUrls(null);
  adhaCodeLoginInFlight = null;
}

export function endNativeAdhaLogin(): void {
  nativeFlowPhase = 'idle';
  authenticatorOpened = false;
  storedAuthenticatorUrls = null;
  persistResumeUrls(null);
}

export function getNativeWebViewSessionId(): string {
  return nativeWebViewSessionId;
}

export function isAwaitingAuthenticatorReturn(): boolean {
  return nativeFlowPhase === 'awaiting-return';
}

export function isFreshNativeAdhaLogin(): boolean {
  return nativeFlowPhase === 'fresh-start';
}

/** Resume authenticator return only for this attempt — ignore stale launch URLs. */
export function shouldHandleAuthenticatorAppReturn(): boolean {
  return nativeFlowPhase === 'awaiting-return';
}

export function markAuthenticatorOpened(): void {
  authenticatorOpened = true;
  nativeFlowPhase = 'awaiting-return';
}

export function subscribeAuthenticatorForegroundResume(listener: () => void): () => void {
  foregroundResumeListeners.add(listener);
  return () => {
    foregroundResumeListeners.delete(listener);
  };
}

function notifyForegroundResume(): void {
  if (!authenticatorOpened) {
    return;
  }
  authenticatorOpened = false;
  foregroundResumeListeners.forEach((listener) => listener());
}

export function getAdhaReturnKindFromUrl(url: string): 'success' | 'fail' | null {
  const decoded = (() => {
    try {
      return decodeURIComponent(url);
    } catch {
      return url;
    }
  })();
  const match = /[?&]adhaReturn=(success|fail)/i.exec(decoded);
  if (match?.[1]) {
    return match[1].toLowerCase() as 'success' | 'fail';
  }
  if (/\/--\/uae-pass/i.test(decoded) && /adhaReturn=fail/i.test(decoded)) {
    return 'fail';
  }
  if (/\/--\/uae-pass/i.test(decoded) && /adhaReturn=success/i.test(decoded)) {
    return 'success';
  }
  return null;
}

function schemePrefix(hostOrPath: string): string {
  return `${getBloomAppScheme()}://${hostOrPath}`;
}

export function getAdhaSuccessReturnPrefix(): string {
  return schemePrefix(ADHA_UAE_PASS_SUCCESS_HOST);
}

export function getAdhaFailureReturnPrefix(): string {
  return schemePrefix(ADHA_UAE_PASS_FAILURE_HOST);
}

/** Deep link back into this ADHA WebView after UAE Pass authenticator finishes. */
export function getAdhaAppReturnPrefix(): string {
  return schemePrefix('uae-pass');
}

function urlStartsWithPrefix(url: string, prefix: string): boolean {
  return url.toLowerCase().startsWith(prefix.toLowerCase());
}

export function isAdhaSuccessReturnUrl(url: string): boolean {
  return (
    urlStartsWithPrefix(url, getAdhaSuccessReturnPrefix()) ||
    getAdhaReturnKindFromUrl(url) === 'success' ||
    (urlStartsWithPrefix(url, getAdhaAppReturnPrefix()) &&
      /[?&]adhaReturn=success/i.test(url))
  );
}

export function isAdhaFailureReturnUrl(url: string): boolean {
  return (
    urlStartsWithPrefix(url, getAdhaFailureReturnPrefix()) ||
    getAdhaReturnKindFromUrl(url) === 'fail' ||
    (urlStartsWithPrefix(url, getAdhaAppReturnPrefix()) &&
      /[?&]adhaReturn=fail/i.test(url))
  );
}

export function isAdhaAppReturnUrl(url: string): boolean {
  if (getAdhaReturnKindFromUrl(url)) {
    return true;
  }
  if (isAdhaSuccessReturnUrl(url) || isAdhaFailureReturnUrl(url)) {
    return true;
  }
  if (/\/--\/uae-pass/i.test(url)) {
    return true;
  }
  if (!urlStartsWithPrefix(url, getAdhaAppReturnPrefix())) {
    return false;
  }
  return /[?&](url|adhaReturn|code|accessCode|access_code|error)=/i.test(url);
}

const SUCCESS_URL_KEYS = ['successurl', 'successURL', 'successUrl'] as const;
const FAILURE_URL_KEYS = ['failureurl', 'failureURL', 'failureUrl'] as const;

interface StoredAuthenticatorUrls {
  successUrl: string;
  failureUrl: string;
}

let storedAuthenticatorUrls: StoredAuthenticatorUrls | null = null;

/** True while native ADHA login is in progress (including authenticator return). */
export function isNativeAdhaFlowActive(): boolean {
  return nativeFlowPhase !== 'idle' || Boolean(storedAuthenticatorUrls);
}

const RESUME_STORAGE_KEY = 'adha_uae_pass_native_resume';

function persistResumeUrls(urls: StoredAuthenticatorUrls | null): void {
  void (async () => {
    try {
      if (!urls) {
        await secureStorage.removeItem(RESUME_STORAGE_KEY);
        return;
      }
      await secureStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify(urls));
    } catch {
      // Resume URLs are best-effort; login can still complete from the live deep link.
    }
  })();
}

export function storeAuthenticatorCallbackUrls(successUrl: string, failureUrl: string): void {
  storedAuthenticatorUrls = { successUrl, failureUrl };
  persistResumeUrls(storedAuthenticatorUrls);
}

export function getStoredAuthenticatorUrls(): StoredAuthenticatorUrls | null {
  return storedAuthenticatorUrls;
}

export async function restoreAuthenticatorCallbackUrls(): Promise<StoredAuthenticatorUrls | null> {
  if (nativeFlowPhase === 'fresh-start' || nativeFlowPhase === 'logging-in') {
    storedAuthenticatorUrls = null;
    return null;
  }
  if (storedAuthenticatorUrls) {
    return storedAuthenticatorUrls;
  }
  try {
    const raw = await secureStorage.getItem(RESUME_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredAuthenticatorUrls;
    if (parsed?.successUrl && parsed?.failureUrl) {
      storedAuthenticatorUrls = parsed;
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
}

export function clearStoredAuthenticatorUrls(): void {
  storedAuthenticatorUrls = null;
  persistResumeUrls(null);
}

function findQueryParam(source: string, keys: readonly string[]): { key: string; value: string } | null {
  for (const key of keys) {
    const match = new RegExp(`[?&](${key})=([^&]*)`, 'i').exec(source);
    if (match?.[1] && match[2] != null) {
      try {
        return {
          key: match[1],
          value: decodeURIComponent(match[2].replaceAll('+', ' ')),
        };
      } catch {
        return { key: match[1], value: match[2] };
      }
    }
  }
  return null;
}

function replaceQueryParam(source: string, key: string, nextValue: string): string {
  const encodedNext = encodeURIComponent(nextValue);
  const re = new RegExp(`([?&])${key}=[^&]*`, 'i');
  if (re.test(source)) {
    return source.replace(re, `$1${key}=${encodedNext}`);
  }
  return source;
}

/** Android WebView often emits intent:// rather than the env app scheme. */
export function unwrapUaePassIntentUrl(url: string): string {
  if (!url.startsWith('intent:')) {
    return url;
  }
  const hashIndex = url.indexOf('#');
  const body =
    hashIndex >= 0
      ? url.slice('intent://'.length, hashIndex)
      : url.replace(/^intent:\/\//, '');
  const fragment = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const schemeMatch = /scheme=([^;]+)/.exec(fragment);
  const scheme = schemeMatch?.[1] || getUaePassAppSchemeName();
  return `${scheme}://${body}`;
}

function applyConfiguredAppScheme(url: string): string {
  const target = getUaePassAppSchemeName();
  if (!target) {
    return url;
  }
  for (const name of getUaePassAppSchemeNames()) {
    if (url.toLowerCase().startsWith(`${name}:`)) {
      return url.replace(new RegExp(`^${name}:`, 'i'), `${target}:`);
    }
  }
  return url;
}

function buildParentReturnUrl(kind: 'success' | 'fail'): string {
  // Never use expo-development-client — that reloads JS from splash.
  // Prefer the scheme that actually launched this process, then app.config.
  const scheme = runtimeAppScheme || process.env.EXPO_PUBLIC_APP_SCHEME || '';
  return `${scheme}://uae-pass?adhaReturn=${kind}`;
}

/**
 * UAE Pass app-to-app: rewrite successurl/failureurl so the authenticator
 * opens Bloom again instead of the HTTPS continue URL in the browser.
 *
 * Official wrap: `{scheme}://{path}?url={originalHttpsCallback}`
 * Use lowercase `uae-pass` (not camelCase hosts) — Android lowercases URL hosts
 * and Expo Router would miss `uaePassSuccess`.
 */
export function wrapUaePassAppDeepLink(url: string): string {
  const linkingUri = getConstantsLinkingUri();
  if (linkingUri) {
    rememberRuntimeLink(linkingUri);
  }
  const normalized = applyConfiguredAppScheme(unwrapUaePassIntentUrl(url));
  const success = findQueryParam(normalized, SUCCESS_URL_KEYS);
  const failure = findQueryParam(normalized, FAILURE_URL_KEYS);

  if (!success || !failure) {
    return normalized;
  }

  storeAuthenticatorCallbackUrls(success.value, failure.value);

  const wrappedSuccessUrl = `${buildParentReturnUrl('success')}&url=${encodeURIComponent(success.value)}`;
  const wrappedFailureUrl = `${buildParentReturnUrl('fail')}&url=${encodeURIComponent(failure.value)}`;
  const wrapped = replaceQueryParam(
    replaceQueryParam(normalized, success.key, wrappedSuccessUrl),
    failure.key,
    wrappedFailureUrl,
  );

  return wrapped;
}

function swapUaePassAppScheme(url: string): string {
  const primary = getUaePassAppSchemeName();
  const fallback = getUaePassAppSchemeFallbackName();
  if (!primary || !fallback) {
    return url;
  }
  if (url.toLowerCase().startsWith(`${primary}:`)) {
    return url.replace(new RegExp(`^${primary}:`, 'i'), `${fallback}:`);
  }
  if (url.toLowerCase().startsWith(`${fallback}:`)) {
    return url.replace(new RegExp(`^${fallback}:`, 'i'), `${primary}:`);
  }
  return url;
}

/** Open the UAE Pass app. Detection can fail on EAS builds missing query schemes. */
export async function openUaePassAuthenticatorUrl(url: string): Promise<void> {
  if (!isUaePassAppAvailable() && !isUaePassAppScheme(url)) {
    return;
  }

  const wrapped = wrapUaePassAppDeepLink(url);
  markAuthenticatorOpened();

  try {
    await Linking.openURL(wrapped);
    return;
  } catch {
    // Full deep-link canOpenURL is unreliable on iOS — try the other scheme.
  }

  const swapped = swapUaePassAppScheme(wrapped);
  if (swapped !== wrapped) {
    try {
      await Linking.openURL(swapped);
      return;
    } catch {
      // Staging vs production UAE Pass app mismatch — leave the WebView up.
    }
  }
}

export function extractResumeUrlFromAppReturn(url: string): string | null {
  const fromQuery = firstQueryValue(url, 'url');
  const providerHost = getAdhaUaePassProviderHost();
  if (
    fromQuery &&
    /^https?:/i.test(fromQuery) &&
    providerHost &&
    fromQuery.includes(providerHost)
  ) {
    return fromQuery;
  }
  const stored = getStoredAuthenticatorUrls();
  if (!stored) {
    return null;
  }
  return getAdhaReturnKindFromUrl(url) === 'fail' ? stored.failureUrl : stored.successUrl;
}

export function isNativeAdhaUaePassCallbackUrl(url: string, redirectUri: string): boolean {
  if (!url) {
    return false;
  }

  if (redirectUri && url.startsWith(redirectUri)) {
    return true;
  }

  if (isUaePassAppScheme(url) && /[?&](code|accessCode|access_code|error)=/.test(url)) {
    return true;
  }

  if (isAdhaFailureReturnUrl(url)) {
    return true;
  }

  if (isAdhaSuccessReturnUrl(url)) {
    const parsed = parseNativeCallbackFromUrl(url);
    return Boolean(parsed.code || parsed.error);
  }

  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/$/, '') || '/';
    const hasOAuthParams = Boolean(
      parsed.searchParams.get('code') ||
        parsed.searchParams.get('accessCode') ||
        parsed.searchParams.get('access_code') ||
        parsed.searchParams.get('error') ||
        parsed.searchParams.get('state'),
    );
    const isCallbackPath =
      path.endsWith('/uae-pass') ||
      path.endsWith('/uaepass') ||
      parsed.hostname.toLowerCase() === 'uaepass';
    return isCallbackPath && hasOAuthParams;
  } catch {
    return (
      /\/uae-?pass(\?|$)/i.test(url) &&
      /[?&](code|accessCode|access_code|error|state)=/.test(url)
    );
  }
}
